/**
 * ReciboPostCreacionDialog - Dialog post-creación de orden para generar recibo
 */

import { useTranslation } from "react-i18next";
import { FileText } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useGenerarReciboPdf } from "@/hooks/use-ordenes";

interface ReciboPostCreacionDialogProps {
  open: boolean;
  onClose: () => void;
  ordenId: number;
  ordenNumero: string;
  pacienteNombre: string;
  totalExamenes: number;
}

export function ReciboPostCreacionDialog({
  open,
  onClose,
  ordenId,
  ordenNumero,
  pacienteNombre,
  totalExamenes,
}: ReciboPostCreacionDialogProps) {
  const { t } = useTranslation();
  const descargarRecibo = useGenerarReciboPdf();

  const handleGenerar = () => {
    descargarRecibo.mutate(ordenId, {
      onSuccess: () => onClose(),
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            {t("ordenes.recibos.postCreation.title")}
          </DialogTitle>
          <DialogDescription>{t("ordenes.recibos.postCreation.message")}</DialogDescription>
        </DialogHeader>

        <div className="py-2 px-1 rounded-md bg-muted/50 text-sm text-muted-foreground">
          {t("ordenes.recibos.postCreation.summary", {
            numero: ordenNumero,
            paciente: pacienteNombre,
            count: totalExamenes,
          })}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={descargarRecibo.isPending}>
            {t("ordenes.recibos.postCreation.skip")}
          </Button>
          <Button onClick={handleGenerar} disabled={descargarRecibo.isPending}>
            <FileText className="h-4 w-4 mr-2" />
            {descargarRecibo.isPending
              ? t("ordenes.recibos.generating")
              : t("ordenes.recibos.postCreation.generate")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
